// A qué API le habla este build.
//
// Por defecto, la de PRODUCCIÓN (cuenta de BET, migrada el 2026-09-18). Se
// puede pisar al compilar con `VITE_API_BASE=...` — y eso existe por una sola
// razón: la instancia vieja, en la cuenta de carlos, se compila desde este
// MISMO código pero apuntando a su propia API. Así las dos comparten todo y
// sólo difieren en el servidor, que es justo lo que `lib/instancia.ts` lee para
// decidir si mostrar el cartel de mudanza o la marca de BET.
//
// Ver ../../MIGRACION-A-CUENTA-BET.md.
export const API_BASE = import.meta.env.PROD
  ? (import.meta.env.VITE_API_BASE || 'https://facturacion-api.betspupplier-fs.workers.dev/api')
  : '/api';

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      this.setToken(null);
      window.location.href = '/login';
      throw new Error('No autorizado');
    }

    const data = await response.json();

    if (!response.ok) {
      const err = new Error(data.error || 'Error en la peticion') as any;
      err.status = response.status;
      err.body = data;
      throw err;
    }

    return data;
  }

  // Auth
  async login(email: string, password: string) {
    const data = await this.request<{ token: string; usuario: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.token);
    return data;
  }

  async logout() {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      this.setToken(null);
    }
  }

  async getMe() {
    return this.request<{ usuario: any }>('/auth/me');
  }

  // Facturas
  async getFacturas(params?: Record<string, string>) {
    const searchParams = new URLSearchParams(params);
    return this.request<{
      data: any[];
      total: number;
      page: number;
      limit: number;
      total_pages: number;
    }>(`/facturas?${searchParams}`);
  }

  async getFactura(id: string) {
    return this.request<any>(`/facturas/${id}`);
  }

  async getStats(params?: Record<string, string>) {
    const searchParams = new URLSearchParams(params);
    const query = searchParams.toString();
    return this.request<{
      total_facturas: number;
      pendientes: number;
      aprobadas: number;
      rechazadas: number;
      en_espera: number;
      criticas: number;
      monto_total: number;
      monto_pendiente: number;
      monto_aprobado: number;
    }>(`/facturas/stats${query ? `?${query}` : ''}`);
  }

  async uploadFactura(xmlContent: string, replace?: boolean) {
    return this.request<{ message: string; factura: any }>('/facturas/upload', {
      method: 'POST',
      body: JSON.stringify({ xml: xmlContent, ...(replace ? { replace: true } : {}) }),
    });
  }

  async updateEstado(id: string, estado: string, comentario?: string) {
    return this.request<{ message: string }>(`/facturas/${id}/estado`, {
      method: 'PATCH',
      body: JSON.stringify({ estado, comentario }),
    });
  }

  async deleteFactura(id: string) {
    return this.request<{ message: string }>(`/facturas/${id}`, {
      method: 'DELETE',
    });
  }

  async asignarUsuario(id: string, usuarioId: string | null) {
    return this.request<{ message: string }>(`/facturas/${id}/asignar`, {
      method: 'PATCH',
      body: JSON.stringify({ usuario_id: usuarioId }),
    });
  }

  async updateTipoFactura(id: string, tipoFactura: string, subtipoFactura?: string) {
    return this.request<{ message: string }>(`/facturas/${id}/tipo`, {
      method: 'PATCH',
      body: JSON.stringify({ tipo_factura: tipoFactura, subtipo_factura: subtipoFactura }),
    });
  }

  async updateTipoIva(id: string, tipoIva: string) {
    return this.request<{ message: string }>(`/facturas/${id}/iva`, {
      method: 'PATCH',
      body: JSON.stringify({ tipo_iva: tipoIva }),
    });
  }

  async updateLineasIva(id: string, lineasIva: Array<{
    line_id: string;
    description: string;
    subtotal: number;
    tax_amount: number;
    tax_percent: number;
    tax_scheme_id: string;
    tipo_iva: string;
  }>, tipoIva: string, lineasRetencion?: Array<{
    line_id: string;
    cod_retencion: string;
    rtefte: number | null;
    cod_rete: string | null;
    top_rete: number | null;
    po_rete: number | null;
  }>) {
    return this.request<{ message: string }>(`/facturas/${id}/lineas-iva`, {
      method: 'PATCH',
      body: JSON.stringify({ lineas_iva: lineasIva, tipo_iva: tipoIva, lineas_retencion: lineasRetencion }),
    });
  }

  async updateLineasSubtipo(id: string, lineasSubtipo: Array<{
    line_id: string;
    description: string;
    subtipo_factura: string;
  }>, tipoFactura: string, subtipoFactura: string) {
    return this.request<{ message: string }>(`/facturas/${id}/lineas-subtipo`, {
      method: 'PATCH',
      body: JSON.stringify({ lineas_subtipo: lineasSubtipo, tipo_factura: tipoFactura, subtipo_factura: subtipoFactura }),
    });
  }

  async reprocessCufe() {
    return this.request<{ message: string; total: number; updated: number }>('/facturas/reprocess-cufe', {
      method: 'POST',
    });
  }

  // Obtener todos los IDs que coinciden con los filtros (sin paginacion)
  async getExportIds(params?: Record<string, string>) {
    const searchParams = new URLSearchParams(params);
    return this.request<{
      ids: string[];
      facturas: Array<{ id: string; numero: string; tipo_factura: string | null; tipo_iva: string | null; subtipo_factura: string | null }>;
      total: number;
    }>(`/facturas/export-ids?${searchParams}`);
  }

  // Export con items
  async exportFacturas(ids: string[]) {
    return this.request<{
      data: Array<{
        id: string;
        numero: string;
        fecha_emision: string;
        fecha_vencimiento: string | null;
        valor_subtotal: number;
        valor_iva: number;
        valor_total: number;
        estado: string;
        receptor_nit: string | null;
        receptor_nombre: string | null;
        tipo_factura: string | null;
        subtipo_factura: string | null;
        tipo_iva: string | null;
        tipo_documento: string | null;
        tax_percent: string | null;
        proveedor_nit: string | null;
        proveedor_razon_social: string | null;
        codigo_cta: string;
        cuenta_x_pagar: string;
        cod_retencion: string;
        rtefte: number | null;
        cod_rete: string;
        top_rete: number | null;
        po_rete: number | null;
        cod_cc: string;
        items: Array<{
          description: string;
          sellers_item_id: string;
          internal_code: string | null;
          equivalency_description: string | null;
          standard_item_id: string;
          quantity: number;
          price: number;
          subtotal: number;
          tax_amount: number;
          total: number;
          tipo_iva: string;
          tax_percent_line: number | null;
          subtipo_factura: string;
          retencion_line: {
            cod_retencion: string;
            rtefte: number | null;
            cod_rete: string | null;
            top_rete: number | null;
            po_rete: number | null;
          } | null;
        }>;
      }>;
    }>('/facturas/export', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  // Acciones Bulk
  async bulkUpdateEstado(ids: string[], estado: string) {
    return this.request<{ message: string; updated: number }>('/facturas/bulk/estado', {
      method: 'PATCH',
      body: JSON.stringify({ ids, estado }),
    });
  }

  async bulkDelete(ids: string[]) {
    return this.request<{ message: string; deleted: number }>('/facturas/bulk/delete', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
  }

  // Usuarios
  async getUsuarios() {
    return this.request<{ usuarios: any[] }>('/usuarios');
  }

  async createUsuario(data: { nombre: string; email: string; password: string; rol?: string }) {
    return this.request<{ message: string; usuario: any }>('/usuarios', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUsuario(id: string, data: { nombre?: string; email?: string; rol?: string; activo?: boolean; password?: string }) {
    return this.request<{ message: string }>(`/usuarios/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUsuario(id: string) {
    return this.request<{ message: string }>(`/usuarios/${id}`, {
      method: 'DELETE',
    });
  }

  // Equivalencias de productos
  async lookupEquivalencias(supplierCode: string, externalCodes: string[]) {
    return this.request<{
      data: Array<{ external_code: string; internal_code: string | null }>;
    }>('/equivalencias/bulk-lookup', {
      method: 'POST',
      body: JSON.stringify({ supplier_code: supplierCode, external_codes: externalCodes }),
    });
  }

  async saveEquivalencias(supplierCode: string, items: Array<{ external_code: string; internal_code: string; description?: string }>) {
    return this.request<{
      data: { imported: number; total_submitted: number; errors: any[] };
    }>('/equivalencias/import', {
      method: 'POST',
      body: JSON.stringify({ supplier_code: supplierCode, items }),
    });
  }

  // --- Catalogo maestro de suppliers ---
  async getSuppliers(params?: Record<string, string>) {
    const searchParams = new URLSearchParams(params);
    return this.request<{
      data: any[];
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
    }>(`/suppliers?${searchParams}`);
  }

  async getRetentionOptions() {
    return this.request<{ data: Array<{
      cod_retencion: string;
      rtefte: number | null;
      cod_rete: string | null;
      top_rete: number | null;
      po_rete: number | null;
    }> }>('/suppliers/retention-options');
  }

  async searchSuppliers(query: string) {
    return this.request<{ data: any[] }>(`/suppliers/search?q=${encodeURIComponent(query)}`);
  }

  async searchSuppliersByNit(nit: string) {
    return this.request<{ data: any[] }>(`/suppliers/search?nit=${encodeURIComponent(nit)}`);
  }

  async getSupplier(id: number) {
    return this.request<{ data: any }>(`/suppliers/${id}`);
  }

  async checkFacturasSuppliers(ids: string[]) {
    return this.request<{
      missing: Array<{ id: string; numero: string; proveedor_nit: string; proveedor_razon_social: string }>;
    }>('/facturas/check-suppliers', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  async checkFacturaSupplier(facturaId: string) {
    return this.request<{
      found: boolean;
      supplier?: any;
      proveedor_nit: string | null;
      proveedor_razon_social: string | null;
    }>(`/facturas/${facturaId}/check-supplier`);
  }

  async registerSupplier(data: any) {
    return this.request<{ data: any }>('/suppliers/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createSupplier(data: any) {
    return this.request<{ data: any }>('/suppliers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSupplier(id: number, data: any) {
    return this.request<{ data: any }>(`/suppliers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSupplier(id: number) {
    return this.request<{ success: boolean }>(`/suppliers/${id}`, {
      method: 'DELETE',
    });
  }

  async importSuppliers(items: any[]) {
    return this.request<{
      data: { imported: number; total_submitted: number; valid_items: number; errors: string[] };
    }>('/suppliers/import', {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  // --- Equivalencias CRUD ---
  async getEquivalencias(params: { supplier_id: number; category?: string; page?: number; per_page?: number; search?: string }) {
    const searchParams = new URLSearchParams();
    searchParams.set('supplier_id', String(params.supplier_id));
    if (params.category) searchParams.set('category', params.category);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.per_page) searchParams.set('per_page', String(params.per_page));
    if (params.search) searchParams.set('search', params.search);
    return this.request<{
      data: any[];
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
    }>(`/equivalencias?${searchParams}`);
  }

  async createEquivalencia(data: { supplier_id: number; category?: string; external_code: string; internal_code: string; description?: string; image_url?: string }) {
    return this.request<{ data: any }>('/equivalencias', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEquivalencia(id: number, data: any) {
    return this.request<{ data: any }>(`/equivalencias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEquivalencia(id: number) {
    return this.request<{ success: boolean }>(`/equivalencias/${id}`, {
      method: 'DELETE',
    });
  }

  async importEquivalencias(supplierCode: string, category: string, items: Array<{ external_code: string; internal_code: string; description?: string }>) {
    return this.request<{
      data: { imported: number; total_submitted: number; errors: any[] };
    }>('/equivalencias/import', {
      method: 'POST',
      body: JSON.stringify({ supplier_code: supplierCode, category, items }),
    });
  }

  // Robot link (publico, sin auth) - link remoto para el boton "Activar Robot"
  async getRobotLink() {
    return this.request<{ link: string }>('/robot-link');
  }

  // ERP sync
  async syncErp(numero: string) {
    return this.request<{ success: boolean; numero: string; erp: boolean }>('/ERP', {
      method: 'POST',
      body: JSON.stringify({ numero }),
    });
  }

  async unsyncErp(numero: string) {
    return this.request<{ success: boolean; numero: string; erp: boolean }>('/ERP2', {
      method: 'POST',
      body: JSON.stringify({ numero }),
    });
  }

  // --- Analytics (facturas) ---
  async getComprasPorPeriodo(params?: Record<string, string>) {
    const searchParams = new URLSearchParams(params);
    return this.request<{ data: any[] }>(`/analytics/compras-por-periodo?${searchParams}`);
  }

  async getComprasPorProveedor(params?: Record<string, string>) {
    const searchParams = new URLSearchParams(params);
    return this.request<{ data: any[] }>(`/analytics/compras-por-proveedor?${searchParams}`);
  }

  async getFacturasPorEstado(params?: Record<string, string>) {
    const searchParams = new URLSearchParams(params);
    return this.request<{ data: any[] }>(`/analytics/facturas-por-estado?${searchParams}`);
  }

  // Enriquecimiento manual
  async enrichProveedores() {
    return this.request<{
      success: boolean;
      message: string;
      total_pending: number;
      total_synced: number;
    }>('/config/enrich-proveedores', {
      method: 'POST',
    });
  }

  // Proveedores
  async getProveedores() {
    return this.request<{ proveedores: any[] }>('/proveedores');
  }

  // Servicios
  async getServicios() {
    return this.request<{ servicios: any[] }>('/servicios');
  }

  async createServicio(data: { codigo: string; nombre: string; descripcion?: string }) {
    return this.request<{ servicio: any }>('/servicios', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateServicio(id: string, data: { codigo?: string; nombre?: string; descripcion?: string; activo?: boolean }) {
    return this.request<{ servicio: any }>(`/servicios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteServicio(id: string) {
    return this.request<{ success: boolean }>(`/servicios/${id}`, {
      method: 'DELETE',
    });
  }

  // Codigos contables (subtipo_factura de gastos)
  async getCodigosContables() {
    return this.request<{ codigos: { id: string; codigo: string; nombre: string; activo: boolean }[] }>('/codigos-contables');
  }

  async createCodigoContable(data: { codigo: string; nombre: string }) {
    return this.request<{ codigo: any }>('/codigos-contables', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCodigoContable(id: string, data: { codigo?: string; nombre?: string; activo?: boolean }) {
    return this.request<{ codigo: any }>(`/codigos-contables/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCodigoContable(id: string) {
    return this.request<{ success: boolean }>(`/codigos-contables/${id}`, {
      method: 'DELETE',
    });
  }

  // Configuracion
  async getGmailConfig() {
    return this.request<{
      client_id: string;
      client_secret: { set: boolean; hint: string };
      refresh_token: { set: boolean; hint: string };
    }>('/config/gmail');
  }

  async updateGmailConfig(data: { client_id?: string; client_secret?: string; refresh_token?: string }) {
    return this.request<{ success: boolean; message: string }>('/config/gmail', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async exchangeGmailCode(code: string, redirectUri: string) {
    return this.request<{ success: boolean; message: string }>('/config/gmail/exchange-code', {
      method: 'POST',
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });
  }

  // Gmail test connection
  async testGmailConnection() {
    return this.request<{
      success: boolean;
      message: string;
      email?: string;
    }>('/gmail/test', {
      method: 'POST',
    });
  }

  // Gmail sync (legacy)
  async syncGmail() {
    return this.request<{
      success: boolean;
      message: string;
      emails_found: number;
      xmls_extracted: number;
      created: Array<{ facturaId?: string; numero?: string; proveedor?: string; valorTotal?: number }>;
      duplicates: Array<{ numero?: string; error?: string }>;
      errors: Array<{ error?: string }>;
    }>('/gmail/sync', {
      method: 'POST',
    });
  }

  // Gmail wizard endpoints
  async gmailFetchEmails(filters?: { unread_only?: boolean; month?: number; year?: number; max_results?: number }) {
    return this.request<{
      success: boolean;
      emails: EmailPreview[];
      count: number;
    }>('/gmail/fetch-emails', {
      method: 'POST',
      body: JSON.stringify(filters || {}),
    });
  }

  async gmailExtract(emailIds: string[]) {
    return this.request<{
      success: boolean;
      attachments_count: number;
      invoices: ParsedInvoicePreview[];
      errors: Array<{ error: string }>;
    }>('/gmail/extract', {
      method: 'POST',
      body: JSON.stringify({ email_ids: emailIds }),
    });
  }

  async gmailCheckDuplicates(numeros: string[]) {
    return this.request<{
      duplicates: Record<string, { fecha_emision: string; valor_total: number; estado: string }>;
    }>('/gmail/check-duplicates', {
      method: 'POST',
      body: JSON.stringify({ numeros }),
    });
  }

  async gmailConfirm(data: {
    invoices: ParsedInvoicePreview[];
    mark_as_read: boolean;
    email_ids: string[];
    replace_numeros?: string[];
  }) {
    return this.request<{
      success: boolean;
      message: string;
      created: Array<{ facturaId?: string; numero?: string; proveedor?: string; valorTotal?: number }>;
      duplicates: Array<{ numero?: string; error?: string }>;
      errors: Array<{ error?: string }>;
    }>('/gmail/confirm', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export interface EmailPreview {
  id: string;
  subject: string;
  from: string;
  date: string;
}

export interface ParsedInvoicePreview {
  numero: string;
  fecha_emision: string;
  fecha_vencimiento?: string;
  invoice_type_code?: string;
  emisor_nit: string;
  emisor_razon_social: string;
  receptor_nit: string;
  receptor_nombre: string;
  valor_subtotal: number;
  valor_iva: number;
  valor_total: number;
  moneda: string;
  cufe?: string;
  xml_content: string;
  source_email_id: string;
  source_filename: string;
}

export const api = new ApiClient();
